/*
 * spectral-upmix.js — window.__SpectralUpmix
 *
 * The Spectral upmix: per-STFT-bin phantom centre + primary/ambient rear from
 * a stereo pair. ONE implementation for /channel-converter («Спектральный»,
 * and its 3.0 / 3.1 stem targets) and /surround-disc; the bench
 * (scripts/spectral-upmix-bench.mjs) evaluates THIS file, so a number measured
 * there is a number users get. Invariants: tests/js/spectral-upmix.test.mjs;
 * both pages: tests/playwright/channel-converter-spectral-module.mjs.
 *
 * Per bin, from cross-spectra smoothed over time:
 *   centre  — Wiener gain Re(S_LR)⁺ / P_mid on M = (L+R)/2, |C| clamped to
 *             min(|L|,|R|) so a source living in one channel is never centre;
 *   ambient — L = k·P + A_L, R = P + A_R with the ambient capped by the
 *             quieter channel, aligned by the dominant inter-channel delay.
 * extract() returns C, A_L, A_R; assemble() builds the 6-channel spectral
 * space; rowsFor() maps it onto a layout.
 *
 * What was measured before keeping it this way (2026-09-23, 144 MUSDB18 clips
 * rebuilt so the right answer is known: dry vocal, bass, drums at centre,
 * "other" hard-left / half-left / original stereo, decorrelated vocal reverb):
 *   · the centre channel matches the true centre at 10.95 dB (hard scene);
 *     the IDEAL ratio mask at the same resolution reaches 13.30 — the headroom
 *     is real, but no second-order-statistics variant gets there:
 *       smoothing 20…800 ms, centred instead of causal ...... ±0.5 dB, trades
 *       window 46…372 ms, 2–3-band multi-resolution ........ ±0.2 dB, 1.7–2.5× cost
 *       smoothing across ±1…8 bins .......................... ±0.1 dB
 *       mask exponent 0.5 / 0.75 / 2 ....................... moves along the
 *         same curve: more vocal in C = more reverb in C
 *       two-channel MMSE (est:'mwf') ........................ +0.4 dB overall;
 *         vocal +4.6, panned instruments −3.4, but reverb +2.1 in the centre
 *         and centred bass/drums −1.5…−5.5 with a half-panned instrument
 *     so the default stays. Closing the gap needs a model of the sources — the
 *     paid AI surround (Demucs) is that step.
 *   · the reverb that reaches the centre (57 % of its mid) does so while the
 *     vocal sings as much as after it stops (−2.4 vs −2.6 dB) — it is the
 *     per-bin ambiguity, not a lagging mask.
 * Fixed here against the engine this module replaced: frames now cover the
 * file's edges (the first ~140 ms and last ~190 ms used to be passed through
 * unextracted), and window and smoothing scale with the sample rate
 * (unscaled, 192 kHz moved the result by ~1.5 dB; 88.2 kHz by 0.2).
 */
(function (root) {
    'use strict';

    // ── FFT: iterative radix-2 ──────────────────────────────────────────────
    // Twiddles by recurrence, not by table: in V8 a table lookup per butterfly
    // costs more than the two multiplies it saves (8192 points: 0.51 ms
    // recurrence, 0.64 ms table).
    var fftCache = {};
    function getFFT(n) {
        if (fftCache[n]) return fftCache[n];
        var bits = Math.round(Math.log2(n));
        if ((1 << bits) !== n) throw new Error('fft size must be a power of two');
        var rev = new Uint32Array(n);
        for (var i = 0; i < n; i++) {
            var r = 0, x = i;
            for (var b = 0; b < bits; b++) { r = (r << 1) | (x & 1); x >>= 1; }
            rev[i] = r;
        }
        function transform(re, im, inv) {
            var i, j, t, len;
            for (i = 0; i < n; i++) {
                j = rev[i];
                if (j > i) { t = re[i]; re[i] = re[j]; re[j] = t; t = im[i]; im[i] = im[j]; im[j] = t; }
            }
            for (len = 2; len <= n; len <<= 1) {
                var h = len >> 1, ang = (inv ? 2 : -2) * Math.PI / len, wr = Math.cos(ang), wi = Math.sin(ang);
                for (i = 0; i < n; i += len) {
                    var cwr = 1, cwi = 0;
                    for (var k = 0; k < h; k++) {
                        var a = i + k, bb = a + h;
                        var vr = re[bb] * cwr - im[bb] * cwi, vi = re[bb] * cwi + im[bb] * cwr;
                        var ur = re[a], ui = im[a];
                        re[a] = ur + vr; im[a] = ui + vi; re[bb] = ur - vr; im[bb] = ui - vi;
                        var nwr = cwr * wr - cwi * wi; cwi = cwr * wi + cwi * wr; cwr = nwr;
                    }
                }
            }
            if (inv) { var q = 1 / n; for (i = 0; i < n; i++) { re[i] *= q; im[i] *= q; } }
        }
        return (fftCache[n] = { n: n, transform: transform });
    }

    // ── Dominant inter-channel delay (samples) ──────────────────────────────
    // The ambient decision reads the IN-PHASE part of the cross-spectrum. A
    // spaced-pair recording pans a direct source by TIME; where its phase
    // reaches 90° the real part is zero and it reads as ambience. Leak of a
    // no-ambience source into the rear before alignment: −8.5 / −0.4 / −0.3 dB
    // at 0.2 / 0.8 / 2 ms; after: −52.2 / −40.4 / −32.4. Confidence is the
    // NORMALISED correlation at the best lag, threshold low on purpose
    // (aligning for nothing costs ~1.5 dB of rear level; not aligning a real
    // delay puts a direct source behind the listener).
    var DELAY_MAX_MS = 5, DELAY_MIN_CONFIDENCE = 0.3;
    function dominantDelaySamples(L, R, sr) {
        var maxLag = Math.round(DELAY_MAX_MS / 1000 * sr);
        var n = L.length;
        if (n < maxLag * 4) return 0;
        var lags = 2 * maxLag + 1;
        var want = Math.max(4096, Math.floor(30000000 / lags));
        var step = Math.max(1, Math.floor((n - 2 * maxLag) / want));
        var best = 0, bestV = -Infinity, eL = 0, eR = 0, i, j;
        for (i = maxLag; i < n - maxLag; i += step) { eL += L[i] * L[i]; eR += R[i] * R[i]; }
        var denom = Math.sqrt(eL * eR) + 1e-30;
        for (var lag = -maxLag; lag <= maxLag; lag++) {
            var acc = 0;
            for (j = maxLag; j < n - maxLag; j += step) acc += L[j] * R[j + lag];
            if (acc > bestV) { bestV = acc; best = lag; }
        }
        return (bestV / denom >= DELAY_MIN_CONFIDENCE) ? best : 0;
    }

    // ── Parameters ──────────────────────────────────────────────────────────
    // win is for 44.1 kHz and is scaled by the nearest power of two of
    // sr/44100 (8192 → 186 ms at 44.1, 171 at 48, 186 at 88.2 …); smoothing is
    // a time constant (441 ms = the historical 0.9 per 2048-sample hop).
    var DEFAULTS = {
        win: 8192,
        tauMs: 441,
        maskPow: 1,        // centre gain exponent (1 = Wiener)
        est: 'mid',        // 'mid' | 'mwf' (two-channel MMSE — measured, not default; see header)
        srScale: true,
        yieldFrames: 32
    };
    function scaleFor(sr) { return Math.pow(2, Math.round(Math.log2(sr / 44100))); }
    var SURROUND_TARGET_DB = -14, SURROUND_MAX_LIFT_DB = 12;

    // ── Public entry ────────────────────────────────────────────────────────
    // L, R — Float32Array channels. opts: onProgress(frac), alive() → false
    // cancels (throws {cancelledRun:true}), params (see DEFAULTS), probes —
    // bench only: extra {L,R} signals that receive EXACTLY the gains computed
    // from the mix, so a metric can say which source ended up where.
    async function extract(L, R, sr, opts) {
        opts = opts || {};
        var p = {}, key;
        for (key in DEFAULTS) p[key] = DEFAULTS[key];
        if (opts.params) for (key in opts.params) p[key] = opts.params[key];
        var N = L.length;
        var sig = [{ L: L, R: R }].concat(opts.probes || []), nSig = sig.length;
        var outs = sig.map(function () {
            return { C: new Float32Array(N), AL: new Float32Array(N), AR: new Float32Array(N) };
        });
        var W = p.win * (p.srScale ? scaleFor(sr) : 1), HOP = W >> 2, nBins = (W >> 1) + 1;
        var fft = getFFT(W), i, k, s;
        var win = new Float64Array(W), winN = new Float64Array(W);
        for (i = 0; i < W; i++) { win[i] = 0.5 - 0.5 * Math.cos(2 * Math.PI * i / W); winN[i] = win[i] / 1.5; }  // Hann² at 75 % overlap sums to 3/2
        var A = Math.exp(-(HOP / sr) / (p.tauMs / 1000)), A1 = 1 - A;
        var mPow = p.maskPow, mwf = p.est === 'mwf';
        var tauS = dominantDelaySamples(L, R, sr);
        var rotC = new Float64Array(nBins), rotS = new Float64Array(nBins);
        for (k = 0; k < nBins; k++) { var wr = 2 * Math.PI * k / W * tauS; rotC[k] = Math.cos(wr); rotS[k] = Math.sin(wr); }
        var sLL = new Float64Array(nBins), sRR = new Float64Array(nBins), sLRr = new Float64Array(nBins), sLRi = new Float64Array(nBins);
        var spec = new Float64Array(nSig * nBins * 4);                 // Lr Li Rr Ri per signal
        var gCL = new Float64Array(nBins), gCR = new Float64Array(nBins), gAL = new Float64Array(nBins), gAR = new Float64Array(nBins);
        var re = new Float64Array(W), im = new Float64Array(W), re2 = new Float64Array(W), im2 = new Float64Array(W);

        // Frames cover every sample fully: they start W−HOP before 0 and read
        // zeros outside the file (the engine before 2026-09-23 started at 0 and
        // passed the first ~140 ms and the last ~190 ms through unextracted).
        var first = -(W - HOP), nFrames = Math.floor((N - HOP - first) / HOP) + 1;
        for (var f = 0; f < nFrames; f++) {
            var pos = first + f * HOP;
            // Forward: L and R of each signal share one complex FFT (L + jR).
            for (s = 0; s < nSig; s++) {
                var Ls = sig[s].L, Rs = sig[s].R;
                if (pos >= 0 && pos + W <= N) {
                    for (i = 0; i < W; i++) { re[i] = Ls[pos + i] * win[i]; im[i] = Rs[pos + i] * win[i]; }
                } else {
                    for (i = 0; i < W; i++) {
                        var q = pos + i;
                        if (q >= 0 && q < N) { re[i] = Ls[q] * win[i]; im[i] = Rs[q] * win[i]; } else { re[i] = 0; im[i] = 0; }
                    }
                }
                fft.transform(re, im, false);
                var base = s * nBins * 4;
                for (k = 0; k < nBins; k++) {
                    var nk = k === 0 ? 0 : W - k, a = re[k], b = im[k], c = re[nk], d = im[nk], o = base + k * 4;
                    spec[o] = (a + c) * 0.5; spec[o + 1] = (b - d) * 0.5;   // L
                    spec[o + 2] = (b + d) * 0.5; spec[o + 3] = (c - a) * 0.5; // R
                }
            }
            // Gains per bin, from the mix (signal 0) only.
            for (k = 0; k < nBins; k++) {
                var ok = k * 4, Lr = spec[ok], Li = spec[ok + 1], Rr = spec[ok + 2], Ri = spec[ok + 3];
                var ll = Lr * Lr + Li * Li, rr = Rr * Rr + Ri * Ri;
                sLL[k] = A * sLL[k] + A1 * ll; sRR[k] = A * sRR[k] + A1 * rr;
                sLRr[k] = A * sLRr[k] + A1 * (Lr * Rr + Li * Ri); sLRi[k] = A * sLRi[k] + A1 * (Li * Rr - Lr * Ri);
                var xLL = sLL[k], xRR = sRR[k], xr = sLRr[k];
                var Pc = xr > 0 ? xr : 0, wL, wR;
                if (mwf) {
                    // Two-channel MMSE for L = c + l, R = c + r (l, r independent):
                    // ĉ = Pc·(Pr·L + Pl·R) / (Pc·(Pl+Pr) + Pl·Pr); equal to the
                    // mid gain when Pl = Pr. Not the default — see the header.
                    var Pl = xLL - Pc, Pr = xRR - Pc;
                    if (Pl < 0) Pl = 0; if (Pr < 0) Pr = 0;
                    var den = Pc * (Pl + Pr) + Pl * Pr;
                    if (den > 1e-24) { wL = Pc * Pr / den; wR = Pc * Pl / den; } else { wL = wR = 0.5; }
                } else {
                    var m = Pc / ((xLL + xRR + 2 * xr) / 4 + 1e-12);
                    if (m > 1) m = 1;
                    if (mPow !== 1) m = Math.pow(m, mPow);
                    wL = wR = 0.5 * m;
                }
                // |C| ≤ the quieter channel.
                var cr = wL * Lr + wR * Rr, ci = wL * Li + wR * Ri;
                var cm = Math.sqrt(cr * cr + ci * ci), lim = Math.sqrt(ll < rr ? ll : rr);
                var cs = (cm > lim && cm > 0) ? lim / cm : 1;
                gCL[k] = wL * cs; gCR[k] = wR * cs;

                // Ambient. The cap is the difference between "ambience" and "a
                // hard-panned guitar": equal-power ambience cannot exceed the
                // quieter channel (unbounded, a hard-left source reached the
                // rear at −0.1 dB; bounded, −338 dB).
                var dLR = xLL - xRR;
                var xLR = tauS ? (xr * rotC[k] + sLRi[k] * rotS[k]) : xr;
                var capA = xLL < xRR ? xLL : xRR, Aamb;
                if (xLR > 1e-14) {
                    // Positive root of xLR·k² − dLR·k − xLR = 0, in the form that
                    // does not subtract nearly equal numbers when dLR < 0 (the
                    // textbook form's error shows only at extreme level
                    // differences: −62…−81 dB on the rear — hygiene, not sound).
                    var sq = Math.sqrt(dLR * dLR + 4 * xLR * xLR);
                    var kk = dLR >= 0 ? (dLR + sq) / (2 * xLR) : (2 * xLR) / (sq - dLR);
                    var Pp = xLR / kk, a1 = xRR - Pp, a2 = xLL - kk * kk * Pp;
                    Aamb = a1 < a2 ? a1 : a2;
                } else {
                    Aamb = capA;          // nothing shared: what the channels have in common is ambience
                }
                if (!(Aamb > 0)) Aamb = 0;
                if (Aamb > capA) Aamb = capA;
                // Wiener gains are POWER ratios; the signal gain is the root.
                gAL[k] = Math.sqrt(Math.min(1, Aamb / (xLL + 1e-14)));
                gAR[k] = Math.sqrt(Math.min(1, Aamb / (xRR + 1e-14)));
            }
            // Inverse: C + j·A_L in one FFT, A_R in a second; overlap-add.
            var i0 = pos < 0 ? -pos : 0, i1 = pos + W > N ? N - pos : W;
            for (s = 0; s < nSig; s++) {
                var sb = s * nBins * 4;
                for (k = 0; k < nBins; k++) {
                    var so = sb + k * 4, xLr = spec[so], xLi = spec[so + 1], xRr = spec[so + 2], xRi = spec[so + 3];
                    var Cr = gCL[k] * xLr + gCR[k] * xRr, Ci = gCL[k] * xLi + gCR[k] * xRi;
                    var ALr = gAL[k] * xLr, ALi = gAL[k] * xLi, ARr = gAR[k] * xRr, ARi = gAR[k] * xRi;
                    re[k] = Cr - ALi; im[k] = Ci + ALr; re2[k] = ARr; im2[k] = ARi;
                    if (k > 0 && k < nBins - 1) {
                        re[W - k] = Cr + ALi; im[W - k] = ALr - Ci; re2[W - k] = ARr; im2[W - k] = -ARi;
                    }
                }
                fft.transform(re, im, true);
                fft.transform(re2, im2, true);
                var oC = outs[s].C, oAL = outs[s].AL, oAR = outs[s].AR;
                for (i = i0; i < i1; i++) {
                    var q2 = pos + i, w2 = winN[i];
                    oC[q2] += re[i] * w2; oAL[q2] += im[i] * w2; oAR[q2] += re2[i] * w2;
                }
            }
            if ((f + 1) % p.yieldFrames === 0) {
                if (opts.alive && !opts.alive()) throw Object.assign(new Error('cancelled'), { cancelledRun: true });
                if (opts.onProgress) { try { opts.onProgress(Math.min(1, (f + 1) / nFrames)); } catch (_) {} }
                await new Promise(function (res) { setTimeout(res, 0); });   // keep the page responsive
            }
        }
        // Rear level: ONE gain per file lifting the measured ambient toward a
        // target, never past a ceiling, never attenuating (a moving gain would
        // pump; a fixed make-up cannot serve both a dry and a wet mix — the raw
        // ambient measured −27.7 … −8.0 dB across the bench).
        var main = outs[0], ampA = 0, ampIn = 0;
        for (i = 0; i < N; i++) {
            ampA += main.AL[i] * main.AL[i] + main.AR[i] * main.AR[i];
            ampIn += L[i] * L[i] + R[i] * R[i];
        }
        var haveDb = 10 * Math.log10((ampA + 1e-30) / (ampIn + 1e-30));
        var liftDb = Math.min(Math.max(SURROUND_TARGET_DB - haveDb, 0), SURROUND_MAX_LIFT_DB);
        return {
            outC: main.C, outAL: main.AL, outAR: main.AR,
            ambGain: Math.pow(10, liftDb / 20), ambRawDb: haveDb, tauS: tauS,
            probes: outs.slice(1)
        };
    }

    // ── The 6-channel spectral space ─────────────────────────────────────────
    // 0 L' = L − C, 1 R' = R − C, 2 C_hp (centre above 160 Hz), 3 C_lp (centre
    // bass, the complementary residual), 4 A_L, 5 A_R (ambient, UNscaled — the
    // level decision lives in the rows). wet < 1 blends the Wiener centre toward
    // the passive (L+R)/2 BEFORE the bass split, so wide + centre = original at
    // any wet. The centre highpass is a REAL biquad: a lowpass-residual leaves
    // a phase-quadrature bass tail (measured).
    // dest — optional [Lp, Rp, Chp, Clp] to write into (an AudioBuffer's
    // channels), so the page never holds a second full-length copy.
    function assemble(core, L, R, sr, wet, dest) {
        var N = core.outC.length, i, c;
        var cEff = core.outC;
        if (!(wet >= 0.999)) {
            cEff = new Float32Array(N);
            for (i = 0; i < N; i++) cEff[i] = wet * core.outC[i] + (1 - wet) * (L[i] + R[i]) * 0.5;
        }
        var fc = 160, w = 2 * Math.PI * fc / sr, cosw = Math.cos(w), sinw = Math.sin(w), Q = 0.7071, al = sinw / (2 * Q);
        var b0 = (1 + cosw) / 2, b1 = -(1 + cosw), b2 = (1 + cosw) / 2, a0 = 1 + al, a1 = -2 * cosw, a2 = 1 - al;
        var Lp = dest ? dest[0] : new Float32Array(N), Rp = dest ? dest[1] : new Float32Array(N);
        var Chp = dest ? dest[2] : new Float32Array(N), Clp = dest ? dest[3] : new Float32Array(N);
        var x1 = 0, x2 = 0, y1 = 0, y2 = 0;
        for (i = 0; i < N; i++) {
            c = cEff[i];
            var h = (b0 * c + b1 * x1 + b2 * x2 - a1 * y1 - a2 * y2) / a0;
            x2 = x1; x1 = c; y2 = y1; y1 = h;
            Lp[i] = L[i] - c; Rp[i] = R[i] - c; Chp[i] = h; Clp[i] = c - h;
        }
        return [Lp, Rp, Chp, Clp, core.outAL, core.outAR];
    }

    // ── Rows over the spectral space, per target ─────────────────────────────
    // A row is [[input, gain], …] or {taps, lp, hp, dly}.
    //   3.1 — clean vocal centre (C_hp) + a 4th channel = the centre bass
    //         lowpassed at 160 (the extra lowpass removes the biquad
    //         phase-ghost of the vocal the raw C_lp carries at ~−20 dB). Not
    //         bit-lossless — correct for a stem target.
    //   3.0 — FC = the FULL centre (centred bass belongs in the centre image;
    //         a permanent highpass here thinned the vocal −5 dB at 130 Hz). No
    //         rear, so the ambience stays in the wides.
    //   5.1 / 7.1 — fronts = wide minus its ambient (otherwise the ambience
    //         plays from every speaker); LFE written −10 dB (receivers add +10);
    //         surrounds = the ambience itself, delayed 15 ms (sides) and 27 ms
    //         (backs) — depth from delay, not from inventing a second ambience.
    // Measured when the ambient rear replaced the anti-phase (Hafler) rear,
    // 2026-08-29: hard-left source in the surrounds −3.2 → −338 dB, centre
    // source −31.7 → −183 dB, SL/SR correlation −0.95 → ~0, 5.1 folded back to
    // stereo −9.3 → −337 dB.
    var ROWS = {
        '3.1': [ [[0, 1]], [[1, 1]], [[2, 1]], { taps: [[3, 1]], lp: 160 } ],
        '3.0': [ [[0, 1]], [[1, 1]], [[2, 1], [3, 1]] ],
        '5.1': [ [[0, 1], [4, -1]], [[1, 1], [5, -1]], [[2, 1], [3, 1]],
                 { taps: [[0, .158], [1, .158], [2, .2236], [3, .2236]], lp: 120 },
                 { taps: [[4, 1]], dly: 15 }, { taps: [[5, 1]], dly: 15 } ],
        '7.1': [ [[0, 1], [4, -1]], [[1, 1], [5, -1]], [[2, 1], [3, 1]],
                 { taps: [[0, .158], [1, .158], [2, .2236], [3, .2236]], lp: 120 },
                 { taps: [[4, .7]], dly: 15 }, { taps: [[5, .7]], dly: 15 },
                 { taps: [[4, .7]], dly: 27 }, { taps: [[5, .7]], dly: 27 } ]
    };
    // 3.0 with the wide LF-cut option: wides highpassed at 160, centre FULL.
    var ROWS_30_LFCUT = [ { taps: [[0, 1]], hp: 160 }, { taps: [[1, 1]], hp: 160 }, [[2, 1], [3, 1]] ];

    function normRow(c) { return Array.isArray(c) ? { taps: c, lp: 0, hp: 0, dly: 0 } : { taps: c.taps, lp: c.lp || 0, hp: c.hp || 0, dly: c.dly || 0 }; }
    // gain — the per-file ambient make-up from extract(): it scales ONLY rows
    // that read nothing but the ambient (inputs 4, 5), so the fronts keep
    // subtracting the unscaled ambient and stay exact.
    // opts.heights — window.__UpmixLaws (height rows for 7.1.4 / 7.1.6).
    // opts.spread — centre width, 0…1 (see spreadCentre).
    function rowsFor(target, gain, opts) {
        opts = opts || {};
        if (target === '3.0' && opts.wideCut) return ROWS_30_LFCUT;
        var rows;
        if (target === '7.1.4' || target === '7.1.6') {
            var laws = opts.heights;
            if (!laws) throw new Error('height rows need __UpmixLaws');
            rows = ROWS['7.1'].concat(target === '7.1.4' ? laws.HEIGHT_ROWS : laws.HEIGHT_ROWS_6);
        } else {
            rows = ROWS[target] || ROWS['5.1'];
        }
        if (gain && gain !== 1) {
            rows = rows.map(function (r) {
                var o = normRow(r);
                var amb = o.taps.length > 0 && o.taps.every(function (t) { return t[0] >= 4; });
                if (!amb) return r;
                return { taps: o.taps.map(function (t) { return [t[0], t[1] * gain]; }), lp: o.lp, hp: o.hp, dly: o.dly };
            });
        }
        return spreadCentre(target, rows, opts.spread);
    }

    // ── Centre width ─────────────────────────────────────────────────────────
    // The extraction puts the whole phantom centre on ONE speaker: a voice that
    // the stereo mix spread between two speakers now comes from a point. Every
    // music upmixer carries a control for that (Pro Logic II «Center Width»,
    // the film panner's «divergence»), and a listener asked for it in so many
    // words — «nicht punktgenau auf den Center, mehr verteilt» (2026-10-07).
    //
    // s = 0 is the extraction as it is (the SAME rows object — nothing is
    // recomputed, every existing render stays bit-identical). s = 1 hands the
    // centre back to the fronts at unity, i.e. FL = L − ambience: the voice is
    // exactly where the stereo mix had it and the centre speaker is silent.
    // Between the two the move is LINEAR — s·C to each front, (1 − s)·C to the
    // centre — because that is the one law under which fronts + centre + rear
    // still add up to the original at every position (the space is built so
    // that L′ + C = L; a constant-power law would break that identity
    // everywhere except the ends).
    //
    // Surround layouts only: 3.0 / 3.1 are stems for a DAW, and a centre stem
    // with part of it moved back into the wides is not a stem any more. The
    // LFE tap of the centre stays — bass management is not image.
    function spreadCentre(target, rows, s) {
        s = +s || 0;
        if (!(s > 0) || target === '3.0' || target === '3.1' || !rows || rows.length < 6) return rows;
        if (s > 1) s = 1;
        var out = rows.slice();
        var fc = normRow(rows[2]);
        var front = function (r) {
            var o = normRow(r);
            return { taps: o.taps.concat(fc.taps.map(function (t) { return [t[0], t[1] * s]; })), lp: o.lp, hp: o.hp, dly: o.dly };
        };
        out[0] = front(rows[0]);
        out[1] = front(rows[1]);
        out[2] = { taps: fc.taps.map(function (t) { return [t[0], t[1] * (1 - s)]; }), lp: fc.lp, hp: fc.hp, dly: fc.dly };
        return out;
    }

    root.__SpectralUpmix = {
        extract: extract,
        assemble: assemble,
        rowsFor: rowsFor,
        ROWS: ROWS,
        DEFAULTS: DEFAULTS,
        dominantDelaySamples: dominantDelaySamples,
        getFFT: getFFT,
        scaleFor: scaleFor
    };
})(typeof window !== 'undefined' ? window : globalThis);
